import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Modal,
  Space,
  Spin,
  Tag,
  Typography,
  message as antMessage,
} from "antd";

import {
  fetchOrgPrompts,
  resetOrgPrompt,
  saveOrgPrompt,
} from "../services/assignmentService";

const { Text, Paragraph } = Typography;

const KEY = "assignment_master";

/**
 * The master prompt — ONE shared copy for the whole team, stored in
 * org_settings.prompts. Everyone can read it; only users with the edit_prompts
 * flag can change it (the server enforces this too, so a hidden button is not
 * the security boundary).
 */
export default function MasterPromptModal({ open, onClose, canEdit }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [value, setValue] = useState("");
  const [original, setOriginal] = useState("");
  const [isOverridden, setIsOverridden] = useState(false);
  const [serverCanEdit, setServerCanEdit] = useState(false);
  const [messageApi, contextHolder] = antMessage.useMessage();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchOrgPrompts();
      const text = data?.prompts?.[KEY] || "";
      setValue(text);
      setOriginal(text);
      setIsOverridden((data?.overriddenKeys || []).includes(KEY));
      setServerCanEdit(Boolean(data?.canEdit));
    } catch (error) {
      messageApi.error(
        error?.payload?.error || error?.message || "Couldn't load the master prompt.",
      );
    } finally {
      setLoading(false);
    }
  }, [messageApi]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const editable = canEdit && serverCanEdit;
  const dirty = value !== original;

  const save = async () => {
    setSaving(true);
    try {
      const data = await saveOrgPrompt(KEY, value);
      const text = data?.prompts?.[KEY] || value;
      setOriginal(text);
      setIsOverridden((data?.overriddenKeys || []).includes(KEY));
      messageApi.success("Master prompt saved for the whole team.");
    } catch (error) {
      messageApi.error(
        error?.payload?.error || error?.message || "Couldn't save.",
      );
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    Modal.confirm({
      title: "Reset the master prompt?",
      content:
        "This discards the team's current prompt and restores the built-in default. Everyone is affected.",
      okText: "Reset",
      okButtonProps: { danger: true },
      onOk: async () => {
        const data = await resetOrgPrompt(KEY);
        const text = data?.prompts?.[KEY] || "";
        setValue(text);
        setOriginal(text);
        setIsOverridden(false);
        messageApi.success("Reset to the default prompt.");
      },
    });
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={900}
      title={
        <Space>
          <span>Master prompt</span>
          {isOverridden ? <Tag color="orange">Customized</Tag> : <Tag>Default</Tag>}
        </Space>
      }
      footer={
        <Space>
          {editable && isOverridden ? (
            <Button danger onClick={reset} disabled={saving}>
              Reset to default
            </Button>
          ) : null}
          <Button onClick={onClose}>Close</Button>
          {editable ? (
            <Button type="primary" loading={saving} disabled={!dirty} onClick={save}>
              Save for everyone
            </Button>
          ) : null}
        </Space>
      }
    >
      {contextHolder}

      <Paragraph type="secondary" style={{ marginTop: -4 }}>
        This is the single prompt every ad in the Assignment tab is analyzed
        against. Put your headline / primary text / description option lists in
        here — the model selects from them verbatim rather than inventing copy.
      </Paragraph>

      {!editable ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="You have read-only access"
          description="Ask an admin to enable “Edit the master prompt” for your account."
        />
      ) : (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Shared across the whole team"
          description="Saving changes the prompt for every user immediately."
        />
      )}

      {loading ? (
        <div style={{ padding: 60, textAlign: "center" }}>
          <Spin />
        </div>
      ) : (
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          readOnly={!editable}
          spellCheck={false}
          style={{
            width: "100%",
            minHeight: 460,
            padding: 14,
            borderRadius: 10,
            resize: "vertical",
            fontFamily: "var(--font-mono, 'JetBrains Mono', monospace)",
            fontSize: 13,
            lineHeight: 1.6,
            color: "var(--app-text)",
            background: editable ? "var(--app-surface-2)" : "var(--app-surface-3)",
            border: `1.5px solid ${dirty ? "#fa8c16" : "var(--app-border)"}`,
            outline: "none",
          }}
        />
      )}

      <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between" }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {value.length.toLocaleString()} characters
        </Text>
        {dirty ? (
          <Text style={{ fontSize: 12, color: "#fa8c16" }}>Unsaved changes</Text>
        ) : null}
      </div>
    </Modal>
  );
}
